import { useEffect, useState, useCallback } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Card, CardContent } from "../components/ui/card";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Calendar as CalendarIcon,
  FileDown,
  Package,
  ShoppingBag,
  TrendingUp,
  DollarSign,
  AlertCircle,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  item_wise_sales_report,
  server_post_data,
} from "../ServiceConnection/serviceconnection";
import * as FileSaver from "file-saver";
import XLSX from "sheetjs-style";

const toApiDate = (d) => format(d, "yyyy-MM-dd");

const buildDateRange = (s, e) => {
  const start = new Date(s);
  start.setHours(0, 0, 0, 0);
  const end = new Date(e);
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

// ── Excel helpers ─────────────────────────────────────────────────────────────
const HEADER_FILL = { fgColor: { rgb: "2D6A4F" } }; // dark green
const SUBTOT_FILL = { fgColor: { rgb: "D8F3DC" } }; // light green
const CATROW_FILL = { fgColor: { rgb: "B7E4C7" } }; // mid green
const WHITE_FILL = { fgColor: { rgb: "FFFFFF" } };
const ALT_FILL = { fgColor: { rgb: "F0FFF4" } };

const bold = (color = "000000") => ({ bold: true, color: { rgb: color } });
const border = () => ({
  top: { style: "thin", color: { rgb: "CCCCCC" } },
  bottom: { style: "thin", color: { rgb: "CCCCCC" } },
  left: { style: "thin", color: { rgb: "CCCCCC" } },
  right: { style: "thin", color: { rgb: "CCCCCC" } },
});

const cellStyle = (fill, font, align = "left") => ({
  fill: { patternType: "solid", ...fill },
  font,
  border: border(),
  alignment: { horizontal: align, vertical: "center", wrapText: true },
});

export default function ItemWiseSalesReport() {
  const { toast } = useToast();

  const [startCal, setStartCal] = useState(new Date());
  const [endCal, setEndCal] = useState(new Date());
  const [filterOption, setFilterOption] = useState("today");
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);

  const [itemWiseReport, setItemWiseReport] = useState([]); // flat  → table
  const [categoryWiseReport, setCategoryWiseReport] = useState([]); // grouped → excel
  const [restaurantName, setRestaurantName] = useState("");
  const [stats, setStats] = useState({
    totalItems: 0,
    totalOrders: 0,
    totalQuantity: 0,
    totalRevenue: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const PER_PAGE = 10;

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchData = useCallback(
    async (startDt, endDt) => {
      if (startDt > endDt) {
        toast({
          title: "Validation Error",
          description: "Start date cannot be after end date.",
          variant: "destructive",
        });
        return;
      }
      setShowLoaderAdmin(true);
      const fd = new FormData();
      fd.append("start_date", toApiDate(startDt));
      fd.append("end_date", toApiDate(endDt));
      try {
        const res = await server_post_data(item_wise_sales_report, fd);
        if (res.data.error) {
          toast({
            title: "Error",
            description: res.data.message,
            variant: "destructive",
          });
          setItemWiseReport([]);
          setCategoryWiseReport([]);
          setStats({
            totalItems: 0,
            totalOrders: 0,
            totalQuantity: 0,
            totalRevenue: 0,
          });
        } else {
          const d = res.data.data;
          const flat = d.item_wise_report || [];
          const grouped = d.category_wise_report || [];
          setItemWiseReport(flat);
          setCategoryWiseReport(grouped);
          setRestaurantName(d.restaurant_name || "");
          const totalOrders = flat.reduce(
            (s, i) => s + parseInt(i.order_count || 0),
            0,
          );
          setStats({
            totalItems: d.total_records || flat.length,
            totalOrders,
            totalQuantity: d.total_items_sold || 0,
            totalRevenue: d.total_revenue_from_items || 0,
          });
          setCurrentPage(1);
        }
      } catch (e) {
        console.error(e);
        toast({
          title: "Error",
          description: "Network error occurred",
          variant: "destructive",
        });
      } finally {
        setShowLoaderAdmin(false);
      }
    },
    [toast],
  );

  // ── Filter ───────────────────────────────────────────────────────────────
  const handleFilterChange = useCallback(
    (sel) => {
      setFilterOption(sel);
      if (sel === "custom") return;
      const t = new Date();
      let s, e;
      if (sel === "today") {
        s = new Date(t);
        e = new Date(t);
      } else if (sel === "yesterday") {
        const y = new Date(t);
        y.setDate(y.getDate() - 1);
        s = new Date(y);
        e = new Date(y);
      } else if (sel === "this_week") {
        s = new Date(t);
        s.setDate(t.getDate() - t.getDay());
        e = new Date(t);
      } else if (sel === "this_month") {
        s = new Date(t.getFullYear(), t.getMonth(), 1);
        e = new Date(t.getFullYear(), t.getMonth() + 1, 0);
      } else if (sel === "this_year") {
        s = new Date(t.getFullYear(), 0, 1);
        e = new Date(t.getFullYear(), 11, 31);
      } else return;
      setStartCal(s);
      setEndCal(e);
      const { start, end } = buildDateRange(s, e);
      fetchData(start, end);
    },
    [fetchData],
  );

  const handleCustomFetch = useCallback(() => {
    if (!startCal || !endCal) return;
    const { start, end } = buildDateRange(startCal, endCal);
    fetchData(start, end);
  }, [startCal, endCal, fetchData]);

  // ── Excel Export (category-wise grouped) ─────────────────────────────────
  const exportToExcel = useCallback(() => {
    if (!categoryWiseReport.length) return;
    const sd = format(startCal, "yyyy-MM-dd");
    const ed = format(endCal, "yyyy-MM-dd");

    // Build rows array + parallel styles array
    const rows = [];
    const styles = []; // one style-row per data-row, one style per cell

    const push = (cells, cellStyles) => {
      rows.push(cells);
      styles.push(cellStyles);
    };

    // ── Meta rows (no style) ─────────────────────────────────────────────
    push([`Date: ${sd} to ${ed}`, "", "", "", "", ""], Array(6).fill({}));
    push(
      [`Restaurant: ${restaurantName}`, "", "", "", "", ""],
      Array(6).fill({}),
    );
    push(["", "", "", "", "", ""], Array(6).fill({}));

    // ── Grand totals row ─────────────────────────────────────────────────
    push(
      [
        "GRAND TOTAL",
        "",
        "",
        "",
        stats.totalQuantity,
        parseFloat(stats.totalRevenue).toFixed(2),
      ],
      [
        cellStyle(SUBTOT_FILL, bold(), "left"),
        cellStyle(SUBTOT_FILL, bold()),
        cellStyle(SUBTOT_FILL, bold()),
        cellStyle(SUBTOT_FILL, bold()),
        cellStyle(SUBTOT_FILL, bold(), "right"),
        cellStyle(SUBTOT_FILL, bold(), "right"),
      ],
    );
    push(["", "", "", "", "", ""], Array(6).fill({}));

    // ── Column header ────────────────────────────────────────────────────
    push(
      [
        "Category",
        "Item Name",
        "Variation",
        "Order Count",
        "Qty.",
        "Total (₹)",
      ],
      [
        cellStyle(HEADER_FILL, bold("FFFFFF"), "left"),
        cellStyle(HEADER_FILL, bold("FFFFFF"), "left"),
        cellStyle(HEADER_FILL, bold("FFFFFF"), "left"),
        cellStyle(HEADER_FILL, bold("FFFFFF"), "right"),
        cellStyle(HEADER_FILL, bold("FFFFFF"), "right"),
        cellStyle(HEADER_FILL, bold("FFFFFF"), "right"),
      ],
    );

    // ── Category groups ──────────────────────────────────────────────────
    categoryWiseReport.forEach((cat) => {
      // Category label row
      push(
        [
          cat.category_name,
          "",
          "",
          "",
          cat.sub_total_qty,
          parseFloat(cat.sub_total_revenue).toFixed(2),
        ],
        [
          cellStyle(CATROW_FILL, bold(), "left"),
          cellStyle(CATROW_FILL, bold()),
          cellStyle(CATROW_FILL, bold()),
          cellStyle(CATROW_FILL, bold()),
          cellStyle(CATROW_FILL, bold(), "right"),
          cellStyle(CATROW_FILL, bold(), "right"),
        ],
      );

      (cat.items || []).forEach((item, idx) => {
        const fill = idx % 2 === 0 ? WHITE_FILL : ALT_FILL;
        push(
          [
            "",
            item.display_name || item.item_name,
            item.variation_name || "-",
            item.order_count || 0,
            item.total_quantity || 0,
            parseFloat(item.total_revenue || 0).toFixed(2),
          ],
          [
            cellStyle(fill, {}),
            cellStyle(fill, { bold: false }),
            cellStyle(fill, { color: { rgb: "666666" } }),
            cellStyle(fill, {}, "right"),
            cellStyle(fill, { bold: true }, "right"),
            cellStyle(fill, { bold: true, color: { rgb: "1B5E20" } }, "right"),
          ],
        );
      });

      // Sub-total row
      push(
        [
          "Sub Total",
          "",
          "",
          "",
          cat.sub_total_qty,
          parseFloat(cat.sub_total_revenue).toFixed(2),
        ],
        [
          cellStyle(SUBTOT_FILL, bold(), "left"),
          cellStyle(SUBTOT_FILL, bold()),
          cellStyle(SUBTOT_FILL, bold()),
          cellStyle(SUBTOT_FILL, bold()),
          cellStyle(SUBTOT_FILL, bold(), "right"),
          cellStyle(SUBTOT_FILL, bold(), "right"),
        ],
      );
      push(["", "", "", "", "", ""], Array(6).fill({}));
    });

    // ── Build worksheet ──────────────────────────────────────────────────
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [
      { wch: 26 },
      { wch: 36 },
      { wch: 18 },
      { wch: 14 },
      { wch: 10 },
      { wch: 14 },
    ];

    // Apply cell styles
    styles.forEach((rowStyles, r) => {
      rowStyles.forEach((style, c) => {
        if (!style || !Object.keys(style).length) return;
        const addr = XLSX.utils.encode_cell({ r, c });
        if (!ws[addr]) ws[addr] = { v: "", t: "s" };
        ws[addr].s = style;
      });
    });

    const wb = { Sheets: { "Item Sales": ws }, SheetNames: ["Item Sales"] };
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    FileSaver.saveAs(
      new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      }),
      `item_wise_sales_report_${sd}.xlsx`,
    );
    toast({ title: "Success", description: "Report exported successfully" });
  }, [categoryWiseReport, startCal, endCal, stats, restaurantName, toast]);

  // ── Pagination ────────────────────────────────────────────────────────────
  const totalPages = Math.ceil(itemWiseReport.length / PER_PAGE);
  const currentData = itemWiseReport.slice(
    (currentPage - 1) * PER_PAGE,
    currentPage * PER_PAGE,
  );
  const goPage = (p) => {
    if (p >= 1 && p <= totalPages) setCurrentPage(p);
  };

  // ── Init ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const t = new Date();
    const s = new Date(t);
    s.setHours(0, 0, 0, 0);
    const e = new Date(t);
    e.setHours(23, 59, 59, 999);
    fetchData(s, e);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <MainLayout>
      <Header
        title="Item Wise Sales Report"
        subtitle="Analyze sales performance by individual menu items"
      />
      <div className="p-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            {
              label: "Total Items",
              value: stats.totalItems,
              icon: <Package className="w-5 h-5 text-primary" />,
              bg: "bg-primary/10",
            },
            // {
            //   label: "Total Orders",
            //   value: stats.totalOrders,
            //   icon: <ShoppingBag className="w-5 h-5 text-success" />,
            //   bg: "bg-success/10",
            // },
            {
              label: "Total Quantity",
              value: stats.totalQuantity,
              icon: <TrendingUp className="w-5 h-5 text-warning" />,
              bg: "bg-warning/10",
            },
            {
              label: "Total Revenue",
              value: `₹${parseFloat(stats.totalRevenue).toFixed(2)}`,
              icon: <DollarSign className="w-5 h-5 text-success" />,
              bg: "bg-success/10",
            },
          ].map((s, i) => (
            <div key={i} className="stat-card">
              <div className="flex items-center gap-3">
                <div
                  className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center`}
                >
                  {s.icon}
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">
                    {s.value}
                  </p>
                  <p className="text-sm text-muted-foreground">{s.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Filter</Label>
                <Select value={filterOption} onValueChange={handleFilterChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="yesterday">Yesterday</SelectItem>
                    <SelectItem value="this_week">This Week</SelectItem>
                    <SelectItem value="this_month">This Month</SelectItem>
                    <SelectItem value="this_year">This Year</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {filterOption === "custom" && (
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !startCal && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startCal ? (
                          format(startCal, "dd MMM yyyy")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startCal}
                        onSelect={setStartCal}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {filterOption === "custom" && (
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !endCal && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endCal ? (
                          format(endCal, "dd MMM yyyy")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endCal}
                        onSelect={setEndCal}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {filterOption === "custom" && (
                <div className="flex items-end">
                  <Button
                    onClick={handleCustomFetch}
                    className="w-full md:w-auto"
                  >
                    Apply
                  </Button>
                </div>
              )}

              <div
                className={cn(
                  "flex items-end",
                  filterOption !== "custom" && "md:col-span-3",
                )}
              >
                <Button
                  variant="outline"
                  onClick={exportToExcel}
                  className="gap-2 w-full md:w-auto"
                  disabled={!categoryWiseReport.length}
                >
                  <FileDown className="w-4 h-4" /> Export Excel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">Sr.No</TableHead>
                <TableHead className="text-muted-foreground">Item ID</TableHead>
                <TableHead className="text-muted-foreground">
                  Category
                </TableHead>
                <TableHead className="text-muted-foreground">
                  Item Name
                </TableHead>
                <TableHead className="text-muted-foreground">
                  Variation
                </TableHead>
                <TableHead className="text-muted-foreground text-right">
                  Order Count
                </TableHead>
                <TableHead className="text-muted-foreground text-right">
                  Total Qty
                </TableHead>
                <TableHead className="text-muted-foreground text-right">
                  Total Revenue (₹)
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {showLoaderAdmin ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center text-muted-foreground"
                  >
                    Loading...
                  </TableCell>
                </TableRow>
              ) : currentData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center text-muted-foreground"
                  >
                    No data found
                  </TableCell>
                </TableRow>
              ) : (
                currentData.map((row, idx) => (
                  <TableRow key={idx} className="border-border">
                    <TableCell>
                      {(currentPage - 1) * PER_PAGE + idx + 1}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {row.item_id}
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center px-2 py-1 rounded-md bg-warning/10 text-warning text-xs font-medium">
                        {row.category_name || "-"}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium">
                      {row.display_name || row.item_name}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {row.variation_name || "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="inline-flex items-center px-2 py-1 rounded-md bg-primary/10 text-primary text-xs font-medium">
                        {row.order_count || 0}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {row.total_quantity || 0}
                    </TableCell>
                    <TableCell className="text-right font-semibold text-success">
                      ₹{parseFloat(row.total_revenue || 0).toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-border">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * PER_PAGE + 1} to{" "}
                {Math.min(currentPage * PER_PAGE, itemWiseReport.length)} of{" "}
                {itemWiseReport.length} entries
              </div>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goPage(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                {[...Array(totalPages)].map((_, i) => {
                  const p = i + 1;
                  if (
                    p === 1 ||
                    p === totalPages ||
                    (p >= currentPage - 1 && p <= currentPage + 1)
                  )
                    return (
                      <Button
                        key={p}
                        variant={p === currentPage ? "default" : "outline"}
                        size="sm"
                        onClick={() => goPage(p)}
                      >
                        {p}
                      </Button>
                    );
                  if (p === currentPage - 2 || p === currentPage + 2)
                    return (
                      <span key={p} className="px-1">
                        …
                      </span>
                    );
                  return null;
                })}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Notes */}
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm font-semibold text-primary flex items-center gap-2">
                <AlertCircle className="w-4 h-4" /> Report Information:
              </p>
              <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside ml-4">
                <li>
                  Report shows item-wise sales for the selected date range.
                </li>
                <li>Excel export is grouped by category with sub-totals.</li>
                <li>Restaurant #149 uses a 3 AM – 3 AM business day cycle.</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}
